import { LDClient, useLDClient } from "launchdarkly-react-client-sdk";

/**
 * The topic of the event.
 */
type EventTopic =
  | "order--order-details-step"
  | "order--check-selection-step"
  | "order--review-step"
  | "order--order-complete"
  | "order--order-complete-after-creating-package"
  | "order--order-complete-after-navigating-from-apply-link"
  | "application--submit"
  | "application--section";

/**
 * Gets the app name based on the current host.
 * @returns The app name.
 */
function getAppName() {
  const host = window.location.href;

  if (host.includes("client")) {
    return "client-portal";
  }

  if (host.includes("applicant")) {
    return "applicant-portal";
  }

  if (host.includes("case")) {
    return "case-management";
  }

  return "unknown-app";
}

/**
 * Gets the event key for a given topic and extra.
 * @param topic - The topic of the event.
 * @param extra - Extra information to add to the event.
 */
function getEventKey(topic: EventTopic, extra?: string) {
  return `${getAppName()}--${topic}${extra ? `--${extra}` : ""}`;
}

/**
 * Starts the time tracker for a given topic and extra.
 * @param topic - The topic of the event.
 * @param extra - Extra information to add to the event.
 */
function startTimeTracker(topic: EventTopic, extra?: string) {
  const key = getEventKey(topic, extra);

  localStorage.setItem(key, new Date().toISOString());
}

/**
 * Stops the time tracker for a given topic and extra.
 * @param ldClient - The LaunchDarkly client.
 * @param topic - The topic of the event.
 * @param extra - Extra information to add to the event.
 */
function stopTimeTracker(
  ldClient: LDClient | undefined,
  topic: EventTopic,
  extra?: string
) {
  const key = getEventKey(topic, extra);
  const startTime = localStorage.getItem(key);

  if (!startTime) {
    return;
  }

  if (!ldClient) {
    return;
  }

  const endTime = new Date();
  const timeTaken = endTime.getTime() - new Date(startTime).getTime();
  const timeTakenInMinutes = timeTaken / 1000 / 60;

  ldClient?.track(
    `${key}--time-spent-in-ms`,
    {
      startTime,
      endTime,
      timeTaken,
      timeTakenInMinutes,
      href: window.location.href,
    },
    timeTaken
  );
  ldClient?.track(
    `${key}--time-spent-in-minutes`,
    {
      startTime,
      endTime,
      timeTaken,
      timeTakenInMinutes,
      href: window.location.href,
    },
    timeTakenInMinutes
  );
  localStorage.removeItem(key);
}

/**
 * Cancels the time tracker for a given topic and extra.
 * @param ldClient - The LaunchDarkly client.
 * @param topic - The topic of the event.
 * @param extra - Extra information to add to the event.
 */
function cancelTimeTracker(
  ldClient: LDClient | undefined,
  topic: EventTopic,
  extra?: string
) {
  const key = getEventKey(topic, extra);
  const startTime = localStorage.getItem(key);

  if (!startTime) {
    return;
  }

  const endTime = new Date();
  const timeTaken = endTime.getTime() - new Date(startTime).getTime();
  const timeTakenInMinutes = timeTaken / 1000 / 60;

  ldClient?.track(
    `${key}--drop-off`,
    {
      startTime,
      endTime,
      timeTaken,
      timeTakenInMinutes,
      href: window.location.href,
    },
    timeTaken
  );
  ldClient?.track(
    `${key}--drop-off-in-minutes`,
    {
      startTime,
      endTime,
      timeTaken,
      timeTakenInMinutes,
      href: window.location.href,
    },
    timeTakenInMinutes
  );
  localStorage.removeItem(key);
}

/**
 * The useTimeTracking hook.
 * @returns The useTimeTracking hook.
 */
export function useTimeTracking() {
  const ldClient = useLDClient();

  return {
    startTimeTracker,
    stopTimeTracker(topic: EventTopic, extra?: string) {
      stopTimeTracker(ldClient, topic, extra);
    },
    cancelTimeTracker(topic: EventTopic, extra?: string) {
      cancelTimeTracker(ldClient, topic, extra);
    },
  };
}
